import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import {
  CANNOT_CHANGE_DURATION_EVENT,
  CANNOT_DELETE_EVENT,
  KEY_RECOIL,
  TYPE_BEAT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
} from '@/shared/config';
import {
  updateMultipleStripReview,
  updateMultipleStripType,
  updateStripReview,
  updateStripType,
} from '@/entities/beat';
import {
  checkEventDataChange,
  getEventEffectdByUpdatedEvent,
  getMultipleEventEffectdByUpdatedEvent,
} from '@/entities/event';
import {
  RHYTHM_EVENT_TAB_ID,
  DEFAULT_CONTROL_MODAL_VALUE,
  selectActiveTab,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectGroupEventChanges,
  selectHolterEvents,
  selectIsLoadingStrip,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';

/**
 * Backs the event-type action bar of the ECG Events tab - artifact, delete, mark reviewed, change duration, reset and the special-event-type dropdown. Resolves the effective selection from the event changes and holter events, derives the show and disable flags from the active tab, group changes, saving state and multi-selection, and registers a hover-gated keydown listener mapping A, R, D and C onto those actions.
 *
 * @returns An object with the tab key, loading and active-tab state, the show and disable flags, the reviewed flag, the special-event-type options and the change, review, reset, modal and paging handlers.
 */
const useButtonTypes = () => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Replaces the pending edits to existing events on the ECG Events tab. Forwards the value to the store's setEventChanges action with the TAB_2 key prepended.
   *
   * @param v - The new event-change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const setSpecialEventTypes = useReportStore((s) => s.actions.setSpecialEventTypes);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the ECG Events list to another page. Forwards the value to the store's setPageIndex action with the TAB_2 key prepended.
   *
   * @param v - The new page index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  /**
   * Reads the events newly created on the ECG Events tab but not yet saved. Applies the selectEventNews selector bound to the TAB_2 key to a one-off report-store snapshot.
   *
   * @returns The list of newly created events.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Replaces the newly created events held by the ECG Events tab. Forwards the value to the store's setEventNews action with the TAB_2 key prepended.
   *
   * @param v - The new event list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Replaces the modal-visibility flags of the ECG Events tab. Forwards the value to the store's setControlModal action with the TAB_2 key prepended.
   *
   * @param v - The new control-modal flags, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads the modal-visibility flags of the ECG Events tab. Applies the selectControlModal selector bound to the TAB_2 key to a one-off report-store snapshot rather than subscribing.
   *
   * @returns The current control-modal flags.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Replaces the event options snapshot the ECG Events tab keeps for undo and redo. Forwards the value to the store's setEventOptions action with the TAB_2 key prepended.
   *
   * @param v - The new event options, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const sletedEvent = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Replaces the strip currently selected on the ECG Events tab. Forwards the value to the store's setSelectedStrip action with the TAB_2 key prepended.
   *
   * @param v - The newly selected event, beat or ECG bookmark, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const eventNews = useReportStore(selectEventNews(keyRecoil));

  // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing idEvent/originalData on some members - atom-type gap.
  const sletedEventAny = sletedEvent as any;
  const selectedStrip: any = useMemo(() => {
    const foundEventChange = eventChanges.find((x) => x.idEvent === sletedEventAny?.idEvent);
    if (foundEventChange) {
      return foundEventChange;
    }
    const foundEvent = holterEvents.find((x) => x.idEvent === sletedEventAny?.idEvent);
    if (foundEvent) {
      return foundEvent;
    }
    return { ...(sletedEventAny || {}), ...(sletedEventAny?.originalData || {}) };
  }, [sletedEvent, eventChanges, holterEvents]);

  const isShowDeleteButton = !CANNOT_DELETE_EVENT.includes(activeButton as TYPE_RHYTHM_EVENT_ENUM);
  const isShowChangeEventDurationButton = !CANNOT_CHANGE_DURATION_EVENT.includes(
    activeButton as any,
  );

  const mergeSelectedStrip = _.filter(
    _.uniqBy([selectedStrip || {}, ...(selectedMultipleStrip || [])], 'idEvent'),
    (x) => !_.isEmpty(x),
  );

  const foundEventChange = useMemo(() => {
    const { event } = getEventEffectdByUpdatedEvent(selectedStrip, eventNews, eventChanges);
    return event;
  }, [eventChanges, selectedStrip, eventNews]);

  const isSelectNewEvent = selectedStrip?.isNew;
  const isNotSelectedStrip = _.isEmpty(mergeSelectedStrip) && !selectedStrip?.idEvent;
  const isEmptyEventList = _.isEmpty(selectedMultipleStrip) && isNotSelectedStrip;
  const isSelectedMultipleStrip = !_.isEmpty(mergeSelectedStrip) && mergeSelectedStrip.length > 1;
  const isGroupEventChanges = !!groupEventChanges.find((x) => x.groupEventType === activeButton);
  // getEventEffectdByUpdatedEvent's return type doesn't account for .type being reassigned to TYPE_BEAT_ENUM.DELETED elsewhere - pre-existing gap.
  const isChangeToDeletedEvent = (foundEventChange?.type as any) === TYPE_BEAT_ENUM.DELETED;
  /**
   * Marks the event action buttons unusable while the current selection is mid-save. Truthy when the single selected event's change entry carries isSaving, or when any multi-selected strip matches an event change that does.
   *
   * @returns The saving flag of the selected event, or the first multi-selected strip whose change is saving, otherwise undefined.
   */
  const isDisableWhenSaving =
    foundEventChange?.isSaving ||
    selectedMultipleStrip.find(
      (x) => eventChanges.find((d) => d?.idEvent === x?.idEvent)?.isSaving,
    );
  const isDisableButton =
    isEmptyEventList ||
    isGroupEventChanges ||
    isSelectNewEvent ||
    isDisableWhenSaving ||
    isLoadingStrip;
  const isDisableChangeDuration =
    isNotSelectedStrip ||
    isChangeToDeletedEvent ||
    isGroupEventChanges ||
    isSelectNewEvent ||
    isDisableWhenSaving ||
    isSelectedMultipleStrip ||
    isLoadingStrip;
  const isDisableChangingButton = isDisableButton || controlModal.isShowChangeDurationButtonType;
  const groupTypeChange = !!groupEventChanges.find((x) => x.groupEventType === activeButton);

  const isShowResetButton = useMemo(
    () =>
      !!foundEventChange ||
      selectedMultipleStrip.find((event: any) => {
        const { event: deleteEvent } = getEventEffectdByUpdatedEvent(
          event,
          eventNews,
          eventChanges,
        );
        if (deleteEvent) {
          return true;
        }
        return !!eventChanges.find((x) => x.idEvent === event?.idEvent);
      }),
    [foundEventChange?.idEvent, selectedMultipleStrip, eventChanges, eventNews],
  );

  const isEventReviewed = useMemo(() => {
    if (selectedMultipleStrip.length > 0) {
      let reviewedCount = 0;
      _.forEach(mergeSelectedStrip, (x) => {
        const ele = eventChanges.find((d) => d.idEvent === x.idEvent);
        if (ele) {
          if (ele.isReviewed) {
            reviewedCount += 1;
          }
        } else if (x.isReviewed) {
          reviewedCount += 1;
        }
      });
      if (reviewedCount === mergeSelectedStrip?.length) return true;
      return false;
    }
    if (selectedStrip?.id) {
      return foundEventChange ? foundEventChange.isReviewed : selectedStrip.isReviewed;
    }
    return false;
  }, [selectedMultipleStrip, selectedStrip, eventChanges, mergeSelectedStrip]);

  /**
   * Applies a new event type, such as artifact or deleted, to the current ECG selection. Uses the multi-strip updater when several strips are selected and the single-strip updater otherwise, writing the result into both the undo/redo event-changes snapshot and the pending event changes.
   *
   * @param type - The rhythm or beat type to assign to the selected events.
   * @returns Nothing.
   */
  const handleChangeStripType = (type: any) => {
    if (selectedMultipleStrip.length > 0) {
      const stripChangesUpdate = updateMultipleStripType({
        stripChanges: eventChanges,
        strips: mergeSelectedStrip,
        type,
      });
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
      return;
    }
    if (selectedStrip?.idEvent) {
      const stripChangesUpdate = updateStripType({
        stripChanges: eventChanges,
        strip: selectedStrip,
        type,
      });
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
    }
  };

  /**
   * Marks the selected ECG events reviewed or un-reviewed. Runs the multi- or single-strip review updater, then compares each result against the original holter event and drops entries that no longer differ, so toggling review back to its original value leaves no pending change; the survivors go into both the undo/redo snapshot and the event changes.
   *
   * @param isReviewed - The review state to apply across the selection.
   * @returns Nothing.
   */
  const onClickMarkReview = (isReviewed: boolean) => {
    if (selectedMultipleStrip.length > 0) {
      const stripChangesUpdate = updateMultipleStripReview({
        stripChanges: eventChanges,
        strips: mergeSelectedStrip,
        isReviewed,
      });
      const listIdRemove: any[] = [];
      _.forEach(mergeSelectedStrip, (selectedStrip: any) => {
        const changedEvent = _.find(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
        const originalEvent = _.find(holterEvents, (x) => x.idEvent === selectedStrip.idEvent);
        if (!checkEventDataChange(originalEvent, changedEvent)) {
          listIdRemove.push(selectedStrip.idEvent);
        }
      });
      _.remove(stripChangesUpdate, (x) => listIdRemove.includes(x.idEvent));
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: stripChangesUpdate,
      }));
      setEventChanges(stripChangesUpdate);
      return;
    }
    if (selectedStrip?.idEvent) {
      const stripChangesUpdate = updateStripReview({
        stripChanges: eventChanges,
        strip: selectedStrip,
        isReviewed,
      });
      const changedEvent = _.find(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
      const originalEvent = _.find(holterEvents, (x) => x.idEvent === selectedStrip.idEvent);
      //* Remove from event change if user only change review to original
      if (checkEventDataChange(originalEvent, changedEvent)) {
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate);
      } else {
        _.remove(stripChangesUpdate, (x) => x.idEvent === selectedStrip.idEvent);
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: stripChangesUpdate,
        }));
        setEventChanges(stripChangesUpdate);
      }
    }
  };

  /**
   * Reverts the selected ECG events to their original holter state. Removes the selection from the pending event changes, cascades through link events to also drop any new or changed event overlapping a reverted one, replaces the new-event list when it shrank, re-points the selected strip at the original holter event, and writes both lists into the undo/redo snapshot.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    const resetEvents: any[] = [];
    const clonedEventNews = _.cloneDeep(getEventNews());
    //* Remove những event đang được select khỏi mảng event changes
    const newEventChanges = eventChanges.filter((x) => {
      if (x.idEvent !== selectedStrip?.idEvent) {
        const foundEventInMultipleSelected = selectedMultipleStrip.find(
          (strip) => strip.idEvent === x.idEvent,
        );
        if (foundEventInMultipleSelected) {
          resetEvents.push(x);
          return false;
        }
        return true;
      }
      resetEvents.push(x);
      return false;
    });

    const linkEvents = getMultipleEventEffectdByUpdatedEvent(
      mergeSelectedStrip,
      eventNews,
      eventChanges,
    );
    _.forEach(linkEvents, (linkEvent: any) => {
      _.remove(clonedEventNews, (x) => {
        if (x.id === linkEvent?.linkEventNew?.id) {
          return true;
        }
        return false;
      });
      _.remove(newEventChanges, (x) => {
        if (x.idEvent === linkEvent?.linkEventChange?.idEvent) {
          return true;
        }
        return false;
      });
    });

    //* Check những event bị remove
    //* New event chắc chắn có link event nếu overlap còn event change thì tùy event bị change duration mới có link event nếu overlap
    resetEvents.forEach((resetEvent: any) => {
      //* Remove những event new / change liên quan tới event bị remove
      //* Trong new event sẽ check những event bị reset nếu liên quan tới new event thì new event đó bị remove
      _.remove(clonedEventNews, (eventNew: any) => {
        const { linkEvents } = eventNew;
        if (
          linkEvents?.length &&
          linkEvents.find((linkEvent: any) => linkEvent.idEvent === resetEvent.idEvent)
        ) {
          _.remove(newEventChanges, (x: any) =>
            linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent),
          );
          return true;
        }
        return false;
      });
      //* Trong event change sẽ check nếu có event liên quan tới event bị reset thì remove event change đó
      if (resetEvent.linkEvents?.length) {
        _.remove(newEventChanges, (x: any) =>
          resetEvent.linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent),
        );
      } else {
        eventChanges.forEach((eventChange: any) => {
          const { linkEvents } = eventChange;
          if (linkEvents?.length && linkEvents.find((x: any) => x.idEvent === resetEvent.idEvent)) {
            _.remove(
              newEventChanges,
              (x: any) =>
                linkEvents.find((linkEvent: any) => linkEvent.idEvent === x.idEvent) ||
                x.idEvent === eventChange.idEvent,
            );
          }
        });
      }
    });
    if (clonedEventNews.length !== getEventNews().length) {
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: clonedEventNews,
      }));
      setEventNews(clonedEventNews);
    }
    setSelectedStrip(holterEvents.find((x) => x.idEvent === selectedStrip?.idEvent) || null);
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventChangesState: newEventChanges,
    }));
    setEventChanges(newEventChanges);
  };

  const eventSpecialTypeOptions = useMemo(() => {
    switch (activeButton) {
      case TYPE_RHYTHM_EVENT_ENUM.TACHY:
        return [
          TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
          TYPE_SPECIAL_EVENT_ENUM.SINUS_TACHY,
          TYPE_SPECIAL_EVENT_ENUM.HIGH_TACHY,
        ];
      case TYPE_RHYTHM_EVENT_ENUM.BRADY:
        return [
          TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
          TYPE_SPECIAL_EVENT_ENUM.SINUS_BRADY,
          TYPE_SPECIAL_EVENT_ENUM.LOW_BRADY,
        ];
      default:
        return [
          TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES,
          TYPE_SPECIAL_EVENT_ENUM.SHORT_PAUSE,
          TYPE_SPECIAL_EVENT_ENUM.LONG_PAUSE,
        ];
    }
  }, [activeButton]);

  /**
   * Switches the special-event-type filter used for tachy, brady and pause events. Ignores a selection equal to the current one, otherwise clones the special-event-types map with the new value, stores it and rewinds the event list to the first page.
   *
   * @param name - Key of the special-event-type entry being changed.
   * @param value - The newly chosen special-event-type.
   * @returns Nothing.
   */
  const onChangeSpecialEventType = useCallback(
    (name: any, value: any) => {
      if ((specialEventTypes as any)[name] === value) return;
      const clonedSpecialEventTypes: any = { ...specialEventTypes };
      clonedSpecialEventTypes[name] = value;
      setSpecialEventTypes(clonedSpecialEventTypes);
      setPageIndex({ index: 0 });
    },
    [specialEventTypes],
  );

  useEffect(() => {
    /**
     * Window keydown handler mapping single keys onto the event-type actions. Ignores auto-repeat, presses while strips are loading, presses outside the hovered rhythm-event tab zone and ctrl or meta combinations, then routes A to artifact, R to toggling review, D to delete and C to opening the change-duration modal, each gated by its own disable flag.
     *
     * @param event - The keydown event, read for repeat, keyCode and the ctrl and meta modifiers.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (event?.repeat || isLoadingStrip) {
        return;
      }

      const tabZone = document.getElementById(RHYTHM_EVENT_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }

      const { keyCode, ctrlKey, metaKey } = event;
      if (ctrlKey || metaKey) {
        return;
      }

      switch (keyCode) {
        case 65: {
          // A
          // Check ctrl and meta to distinguish from ctrl+a to select all
          if (!isDisableChangingButton) {
            handleChangeStripType(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT);
          }
          break;
        }
        case 82: {
          // R
          if (!isDisableChangingButton) {
            onClickMarkReview(!isEventReviewed);
          }
          break;
        }
        case 68: {
          // Delete (D)
          if (!isDisableChangingButton && isShowDeleteButton) {
            handleChangeStripType(TYPE_BEAT_ENUM.DELETED);
          }
          break;
        }
        case 67: {
          // Change duration C
          if (
            !isDisableChangeDuration &&
            !getControlModal().isShowChangeDurationButtonType &&
            isShowChangeEventDurationButton
          ) {
            setControlModal({
              ...DEFAULT_CONTROL_MODAL_VALUE,
              isShowChangeDurationButtonType: true,
            });
          }
          break;
        }
        default:
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedMultipleStrip,
    selectedStrip,
    eventChanges,
    isEventReviewed,
    isLoadingStrip,
    activeButton,
    groupEventChanges,
    isDisableChangingButton,
  ]);

  return {
    keyRecoil,
    isLoadingStrip,
    activeButton,
    isShowDeleteButton,
    isShowChangeEventDurationButton,
    isEventReviewed,
    isShowResetButton,
    isDisableChangingButton,
    isDisableChangeDuration,
    groupTypeChange,
    eventSpecialTypeOptions,
    specialEventTypes,
    onChangeSpecialEventType,
    setControlModal,
    getControlModal,
    handleChangeStripType,
    onClickMarkReview,
    onClickReset,
    setPageIndex,
  };
};

export default useButtonTypes;
